import { useSetLiveModal } from "@/shared/atoms/useLiveModal";
import { useSetModal } from "@/shared/atoms/useModal";
import { RecieptSymbol, courierSymbol, shopSymbol } from "@/shared/const/icons";
import useBelgium from "@/shared/hooks/useBelgium";
import useBranches from "@/shared/hooks/useBranches";
import useCommon from "@/shared/hooks/useCommon";
import useEnabledFormules from "@/shared/hooks/useEnabledFormules";
import useIsAdmin from "@/shared/hooks/useIsAdmin";
import { toOrders } from "@/shared/queries/mapOrders/helpers";
import useMapOverviewSettingsQuery from "@/shared/queries/mapOrders/useMapOverviewSettingsQuery";
import { userRoles } from "@/shared/utils/constant";
import { OrderStatus } from "@/shared/utils/enum";
import {
  asArray,
  capitalizeFirstLetter,
  formatDate,
  getFullAddressV2,
  hexToRGBA,
  numberToRGBA,
  ordersToIds,
} from "@/shared/utils/helpers";
import { Branch, ZipcodesTableDataRequest } from "@/shared/utils/interfaces";
import { CourierGeoLocation, OrderV2 } from "@/shared/v2/queries/interfacesV2";
import {
  getGeoOrder,
  getUniqueOrderColor,
  orderWithGeo,
} from "@/shared/v2/utils/helpers";
import { loadModules } from "esri-loader";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
export const colors = {
  new: [202, 101, 34, 255],
  in_kitchen: [0, 168, 255, 255],
  enroute: [108, 55, 217, 255],
  handed_over: [53, 172, 70, 255],
};

function expandZipcodeRange(begin: string, end: string): string[] {
  const start = parseInt(begin.slice(0, 4), 10);
  const stop = parseInt(end.slice(0, 4), 10);

  const results: string[] = [];
  for (let i = start; i <= stop; i++) {
    results.push(i.toString().padStart(4, "0"));
  }
  return results;
}

function isPc4InRange(pc4: string, begin: string, end: string): boolean {
  const start = parseInt(begin.slice(0, 4), 10);
  const stop = parseInt(end.slice(0, 4), 10);
  const pc = parseInt(pc4, 10);

  return pc >= start && pc <= stop;
}

function findZipcodeRecord(pc4: string, zipcodes: ZipcodesTableDataRequest[]) {
  return zipcodes.find((z) =>
    isPc4InRange(pc4, z.zipcode_range_begin, z.zipcode_range_end)
  );
}

// Alpha used for delivered orders so they fade into the background.
const DELIVERED_OPACITY = 80;

const statusColor = {
  1: colors.new,
  2: colors.new,
  3: colors.in_kitchen,
  4: colors.enroute,
  5: colors.enroute,
  7: colors.in_kitchen,
};

export interface OrderMarker {
  amount: string;
  count: string;
  latitude: string;
  longitude: string;
}

export interface MapProps {
  zoom?: number;
  height?: any;
  branch?: Branch;
  orders?: OrderV2[] | OrderMarker[];
  routes?: OrderV2[];
  couriers?: CourierGeoLocation[];
  onExit?: Function;
  showHeatmap?: boolean;
  showSearchWidget?: boolean;
  showZipcodes?: boolean;
  zipcodes?: ZipcodesTableDataRequest[];
}

const Map = ({
  couriers,
  zoom = 13,
  height = "500px",
  branch = undefined,
  orders = [],
  routes = undefined,
  onExit,
  showHeatmap = false,
  showSearchWidget = true,
  showZipcodes = false,
  zipcodes = [],
}: MapProps) => {
  const setLiveModal = useSetLiveModal();
  const { data } = useMapOverviewSettingsQuery();
  const is_auto_zoom_active = data?.is_auto_zoom_active ?? false;
  const zoomLevel = is_auto_zoom_active ? zoom : (data?.zoom_level ?? zoom);
  const [autoFitEnabled, setAutoFitEnabled] = useState(true);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const history = useHistory();
  const { isBelgium, isPending } = useBelgium();
  const memoizedOrders = useMemo(
    () => (showHeatmap ? toOrders(orders) : orders) as OrderV2[],
    [orders, showHeatmap]
  );
  const isMerge = useEnabledFormules();
  const branches = useBranches();
  const setModal = useSetModal();
  const isAdmin = useIsAdmin([userRoles.ROLE_COURIER_ADMIN]);
  const [followCourier, setFollowCourier] = useState(undefined);
  const { t } = useTranslation("", { keyPrefix: "orders_live" });
  const tCommon = useCommon();
  const [view, setView] = useState(null);
  const courierGraphics =
    view?.graphics
      ?.toArray()
      ?.filter((g) => g?.attributes?.type === "courier" && g?.geometry) ?? [];
  const graphicRef = useRef(null);
  const routeParametsRef = useRef(null);
  const legendsRef = useRef(null);
  const featureLayerRef = useRef(null);

  const featureSetRef = useRef(null);
  const routeRef = useRef(null);
  const expandRef = useRef(null);
  const pointRef = useRef(null);
  const extendRef = useRef(null);

  const isLoaded =
    extendRef.current !== null &&
    legendsRef.current !== null &&
    routeParametsRef.current !== null &&
    featureSetRef.current !== null &&
    routeRef.current !== null &&
    expandRef.current !== null &&
    graphicRef?.current !== null &&
    pointRef?.current !== null &&
    view !== null &&
    featureLayerRef.current !== null;

  const mapDiv = useRef(null);
  const removeGraphics = (cb: (graphic: any) => boolean) => {
    const graphics = view?.graphics?.items?.filter(cb);
    graphics.forEach((marker) => view.graphics.remove(marker));
  };

  const setGraphicOpacity = (graphic, opacity) => {
    if (graphic.symbol) {
      // Delivered orders stay faded, so never restore above their base opacity.
      const targetOpacity = Math.min(
        opacity,
        graphic?.attributes?.baseOpacity ?? 255
      );
      // Check if the graphic has a symbol
      const symbol = graphic.symbol.clone();
      symbol.data.symbol.symbolLayers.forEach((l) => {
        try {
          const s = l.markerGraphics[0].symbol;
          const getLayer = () => {
            return s?.symbolLayers?.[0] ?? s?.symbol?.symbolLayers?.[0];
          };
          const layer = getLayer();
          const markerColor = layer.color;
          layer.color = [...markerColor.slice(0, 3), targetOpacity];
        } catch {
          // Ignore marker layer update errors for individual graphics.
        }
      });

      // Modify the symbol's color based on its type

      // Apply the updated symbol to the graphic
      graphic.symbol = symbol;
    }
  };

  const toggleOpacity = (opacity, order?: OrderV2 | OrderV2[]) => {
    view.graphics
      .filter(
        (graphic) =>
          (order === undefined
            ? true
            : Array.isArray(order)
              ? !ordersToIds(order).includes(graphic.attributes.id)
              : graphic?.attributes?.id !==
                order?.order_information?.item_id) &&
          graphic.attributes.type === "order"
      )
      .forEach((graphic) => {
        setGraphicOpacity(graphic, opacity);
      });
  };

  const removeRoutes = () => {
    removeGraphics((graphic) => graphic.attributes.ObjectID !== undefined);
  };

  const removeCouriers = () => {
    removeGraphics((graphic) => graphic.attributes.type === "courier");
  };

  const removeOrders = () => {
    removeGraphics((graphic) => graphic.attributes.type === "order");
  };

  const getRoute = async (order: OrderV2, start, destination, graphics) => {
    const color: any = getUniqueOrderColor(order);

    const features = graphics;
    const routeUrl =
      "https://route-api.arcgis.com/arcgis/rest/services/World/Route/NAServer/Route_World";
    const routeParams = new routeParametsRef.current({
      stops: new featureSetRef.current({
        features,
      }),
      returnDirections: true,
      directionsLanguage: "nl",
    });

    routeRef.current
      .solve(routeUrl, routeParams)
      .then((data) => {
        if (data.routeResults.length > 0) {
          showRoute(data.routeResults[0].route, color, start, destination);
          showDirections(data.routeResults[0].directions.features);
        }
      })
      .catch((error) => {});
  };

  const showRoute = (routeResult, color = "#0284c7", start, destination) => {
    routeResult.symbol = {
      type: "simple-line",
      color: color,
      width: 5,
    };
    view.graphics.add(routeResult, 0);
    view.goTo([start, destination]);
  };

  const showDirections = (directions) => {
    function showRouteDirections(directions) {
      const directionsList = document.createElement("ol");
      directions.forEach(function (result, i) {
        try {
          const direction = document.createElement("li");
          direction.innerHTML =
            result.attributes.text +
            (result.attributes.length > 0
              ? " (" + result.attributes.length.toFixed(2) + " miles)"
              : "");
          directionsList.appendChild(direction);
        } catch (e) {
          console.error(`Error processing direction ${i}:`, e);
        }
      });
      directionsElement.appendChild(directionsList);
    }

    const directionsElement = document.createElement("div");
    directionsElement.innerHTML = "<h3>Richtingen</h3>";

    directionsElement.classList.add("esri-widget");
    directionsElement.classList.add("esri-widget--panel");
    directionsElement.classList.add("esri-directions__scroller");
    directionsElement.classList.add("directions");
    directionsElement.style.marginTop = "0";
    directionsElement.style.padding = "0 15px";
    directionsElement.style.minHeight = "365px";

    showRouteDirections(directions);

    view.ui.empty("top-right");
    view.ui.add(
      new expandRef.current({
        view: view,
        content: directionsElement,
        expanded: false,
        mode: "floating",
      }),
      "top-right"
    );
  };

  const getOrderDestination = (order: OrderV2) => {
    return new pointRef.current({
      longitude: order?.order_user?.address_geo_coordinates?.[3],
      latitude: order?.order_user?.address_geo_coordinates?.[2],
    });
  };

  const setBranchAndCenter = (branch: Branch, zoomLevel: number) => {
    if (branch) {
      const center = new pointRef.current({
        longitude: branch?.gps_longitude,
        latitude: branch?.gps_latitude,
      });
      removeGraphics((graphic) => {
        if (graphic?.attributes?.type === "order") {
          return false;
        }

        if (graphic?.attributes?.type === "courier") {
          return false;
        }
        if (graphic?.attributes?.type === "branch") {
          return graphic?.attribute?.id !== branch.id;
        }
        return true;
      });
      view.center = center;
      view.goTo({ center, zoom: zoomLevel });
      return addGraphic(center, undefined, undefined, {
        type: "branch",
        id: branch.id,
      });
    }
    return undefined;
  };

  const addGraphic = (
    point,
    data: any = shopSymbol(colors.in_kitchen),
    popupTemplate = undefined,
    attributes: any = {}
  ) => {
    const loadedGraphic = view?.graphics?.items.find(
      (item) => item.attributes.id === attributes?.id
    );
    const isAlreadyAdded = loadedGraphic !== undefined;
    const graphic = new graphicRef.current({
      symbol: {
        type: "cim",
        data,
      },
      geometry: point,
      popupTemplate,
      attributes,
      zIndex: Math.floor(Math.random() * 10) + 1,
    });
    if (isAlreadyAdded) {
      view.graphics.remove(loadedGraphic);
    }
    view.graphics.add(graphic);
  };

  //init map
  useEffect(() => {
    loadModules([
      "esri/views/MapView",
      "esri/WebMap",
      "esri/geometry/Point",
      "esri/config",
      "esri/Graphic",
      "esri/rest/support/RouteParameters",
      "esri/rest/support/FeatureSet",
      "esri/rest/route",
      "esri/widgets/Expand",
      "esri/widgets/Home",
      "esri/widgets/Search",
      "esri/layers/GraphicsLayer",
      "esri/widgets/Legend",
      "esri/layers/FeatureLayer",
      "esri/geometry/Extent",
    ]).then(
      ([
        MapView,
        WebMap,
        Point,
        config,
        Graphic,
        RouteParameters,
        FeatureSet,
        route,
        Expand,
        Home,
        Search,
        GraphicsLayer,
        Legend,
        FeatureLayer,
        Extend,
      ]) => {
        extendRef.current = Extend;
        featureLayerRef.current = FeatureLayer;
        legendsRef.current = Legend;
        routeParametsRef.current = RouteParameters;
        featureSetRef.current = FeatureSet;
        ((routeRef.current = route), (expandRef.current = Expand));
        pointRef.current = Point;
        graphicRef.current = Graphic;
        config.apiKey = import.meta.env.VITE_ESRI_API_KEY;

        const webmap = new WebMap({
          basemap: "arcgis-navigation",
        });

        const view = new MapView({
          container: mapDiv.current,
          map: webmap,
          zoom: zoom || 13,
        });

        const button = document.createElement("button");
        button.innerText = tCommon("settings");
        button.style.padding = "8px 12px";
        button.style.borderRadius = "6px";
        button.style.background = "white";
        button.style.cursor = "pointer";
        button.style.boxShadow = "0 2px 6px rgba(0,0,0,0.2)";

        // 2. Event: React state aanpassen
        button.onclick = () => {
          setLiveModal("");
          setModal("mapSettings");
        };

        // 3. Toevoegen aan ArcGIS UI
        view.ui.add(button, "bottom-right");
        view.ui.add(
          new Home({
            view: view,
          }),
          "top-left"
        );

        if (showZipcodes) {
        }

        if (showSearchWidget) {
          const searchWidget = new Search({
            view: view,
            includeDefaultSources: false,
            locationEnabled: false,
            sources: [
              {
                layer: new GraphicsLayer({
                  graphics: view.graphics.toArray(), // Convert the graphics collection to an array
                }),
                searchFields: ["courierName"], // Field to search
                displayField: "courierName", // Field to display in the results
                exactMatch: true, // Allow partial matches
                outFields: ["*"], // Return all fields
                name: "Courier grahics", // Name of this search source
                placeholder: t("searchCourier"), // Placeholder text in the search bar
                getResults: (params) => {
                  const graphic = view.graphics
                    .toArray()
                    .find(
                      (graphic) =>
                        graphic.attributes.id === params.suggestResult.key
                    );
                  view.goTo({ center: graphic, zoom: 14 });
                  return {
                    feature: graphic,
                    name: graphic.attributes.courierName,
                  };
                },
                getSuggestions: (params) => {
                  // Custom suggestions logic (optional)
                  const searchTerm = params.suggestTerm.toLowerCase();
                  const suggestions = view.graphics
                    .toArray()
                    .filter((graphic) => {
                      const value = graphic.attributes?.courierName || "";
                      return value.toLowerCase().includes(searchTerm);
                    })
                    .map((graphic) => {
                      return {
                        key: `${graphic.attributes.id}`,
                        sourceIndex: params.sourceIndex,
                        text: graphic.attributes.courierName,
                      };
                    });
                  return Promise.resolve(suggestions);
                },
              },
            ],
          });

          view.ui.add(searchWidget, {
            position: "top-right",
          });

          view.on("click", function (event) {
            view.hitTest(event).then(function (response) {
              const object = response.results[0].graphic;

              if (
                response.results.length > 0 &&
                response.results[0].graphic &&
                object?.attributes?.type === "courier"
              ) {
                setFollowCourier(object);
              } else {
                setFollowCourier(undefined);
              }
              if (
                response.results.length > 0 &&
                response.results[0].graphic &&
                object?.attributes?.type === "order"
              ) {
                view.goTo({ center: object, zoom: 14 });
              }
            });
          });
        }

        setView(view);
      }
    );
  }, []);

  useEffect(() => {
    if (branch && isLoaded) {
      view.when(() => {
        setBranchAndCenter(branch, zoomLevel);
      });
    }
  }, [branch, isLoaded, zoomLevel]);

  useEffect(() => {
    let handle;
    if (isLoaded && showZipcodes) {
      const oldLayer = view.map.findLayerById("postcodeLayer");
      if (oldLayer) {
        view.map.remove(oldLayer);
      }
      if (!isPending) {
        if (!isBelgium) {
          const postcodeLayer = new featureLayerRef.current({
            id: "postcodeLayer",
            url: "https://services.arcgis.com/nSZVuSZjHpEZZbRo/arcgis/rest/services/Postcodevlakken_PC4/FeatureServer/0",
            outFields: ["*"],
            renderer: {
              type: "unique-value",
              field: "PC4", // veld in je dataset
              defaultSymbol: {
                type: "simple-fill",
                color: [0, 120, 200, 0.2],
                outline: {
                  color: [0, 120, 200, 1],
                  width: 1,
                },
              },
              uniqueValueInfos: zipcodes.flatMap((zipcode) =>
                expandZipcodeRange(
                  zipcode.zipcode_range_begin,
                  zipcode.zipcode_range_end
                ).map((pc) => ({
                  value: pc,
                  symbol: {
                    type: "simple-fill",
                    color: zipcode?.color_code
                      ? hexToRGBA(zipcode.color_code, 0.4)
                      : [0, 120, 200, 0.2],
                    outline: {
                      color: zipcode?.color_code
                        ? hexToRGBA(zipcode.color_code, 1)
                        : [0, 120, 200, 1],
                      width: 2,
                    },
                  },
                }))
              ),
            },
            labelingInfo: [
              {
                symbol: {
                  type: "text", // TextSymbol
                  color: "black",
                  haloColor: "white",
                  haloSize: "1px",
                  font: {
                    size: 12,
                    weight: "bold",
                  },
                },
                labelExpressionInfo: {
                  expression: "$feature.PC4", // veldnaam van postcode
                },
              },
            ],
          });

          view.map.add(postcodeLayer);
        } else {
          const bePostcodeLayer = new featureLayerRef.current({
            id: "postcodeLayer",
            url: "https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/BEL_Boundaries_2023/FeatureServer/8",
            outFields: ["*"],
            renderer: {
              type: "unique-value",
              valueExpression: `$feature.ID + '-' + $feature.Name`, // veld in je dataset
              defaultSymbol: {
                type: "simple-fill",
                color: [0, 120, 200, 0.2],
                outline: {
                  color: [0, 120, 200, 1],
                  width: 1,
                },
              },
              uniqueValueInfos: zipcodes.flatMap((zipcode) =>
                expandZipcodeRange(
                  zipcode.zipcode_range_begin,
                  zipcode.zipcode_range_end
                ).flatMap((pc) =>
                  asArray(zipcode?.area)?.map((area) => ({
                    value: `${pc}-${capitalizeFirstLetter(area)}`, // bv. "9300-Aalst", "9300-Moorsel"
                    symbol: {
                      type: "simple-fill",
                      color: zipcode?.color_code
                        ? hexToRGBA(zipcode.color_code, 0.4)
                        : [0, 120, 200, 0.2],
                      outline: {
                        color: zipcode?.color_code
                          ? hexToRGBA(zipcode.color_code, 1)
                          : [0, 120, 200, 1],
                        width: 2,
                      },
                    },
                  }))
                )
              ),
            },
            labelingInfo: [
              {
                labelExpressionInfo: {
                  expression: `
                  $feature.ID + " - " + $feature.Name
                `, // check hoe het veld heet in je attribuut-tabel
                },
                symbol: {
                  type: "text",
                  color: "black",
                  haloColor: "white",
                  haloSize: 1,
                  font: { family: "Arial", size: 12, weight: "bold" },
                },
                minScale: 0, // labels altijd zichtbaar
                maxScale: 0,
              },
            ],
          });
          view.map.add(bePostcodeLayer);
        }

        handle = view.on("click", async (event) => {
          const response = await view.hitTest(event);
          const results = response.results.filter(
            (r) => r.graphic.layer.id === "postcodeLayer"
          );

          if (results.length > 0) {
            const graphic = results[0].graphic;
            const attributes = graphic.attributes;
            const zipcode = attributes.PC4 ?? attributes.ID;
            const area = attributes.NAME;
            const matchedZip = findZipcodeRecord(zipcode, zipcodes);
            if (matchedZip) {
              history.push(`/zipcodes/${matchedZip.item_id}`);
            } else {
              history.push(
                `/zipcodes/create?zipcode=${zipcode}${
                  area ? `&area=${area}` : ""
                }`
              );
            }
          }
        });
      }
    }
    return () => {
      handle?.remove();
    };
  }, [isLoaded, showZipcodes, isBelgium, isPending, zipcodes]);

  useEffect(() => {
    if (isLoaded) {
      if (memoizedOrders.length !== 0 && !showHeatmap) {
        let currentGraphics = [
          ...view.graphics._items.filter(
            (item) => item?.attributes?.type === "order"
          ),
        ];

        memoizedOrders
          .filter((order) => order?.order_information?.order_status !== 1)
          .filter((order) => order?.order_information?.delivery_method_id === 1)
          .forEach((order) => {
            try {
              const uniqueColor = getUniqueOrderColor(order);
              const isDelivered =
                order?.order_information?.order_status ===
                OrderStatus.HANDED_OVER;

              const address = getFullAddressV2(order);
              const cord: number[] = [];
              const { lng, lat } = getGeoOrder(order);
              if (lng === undefined || lat === undefined) {
                throw "Order does not have valid geo coordinates";
              }
              cord.push(lng);
              cord.push(lat);
              const point = new pointRef.current({
                longitude: cord[0],
                latitude: cord[1],
              });

              const asap = order?.order_information?.hand_over_is_asap;
              const handOverTime = order?.order_information?.hand_over_time;

              const content = [
                {
                  type: "custom",
                  creator: (graphic) =>
                    `<div>
                    ${
                      isMerge
                        ? `<p>${
                            branches?.find(
                              (br) =>
                                br.branch.id ===
                                order?.order_information?.branch_id
                            ).branch.name
                          }</p>`
                        : ""
                    }
                    <p>${address}</p>
                    <p>
                    <div style='display:${asap ? "block" : "none"};'>
                      Z.S.M
                    </div>
                    <div style='display:${handOverTime ? "block" : "none"};'>
                    ${formatDate(handOverTime, "time")}
                    </div>
                    </p>
                  <div>`,
                },
              ];

              if (isAdmin) {
                content.push({
                  type: "custom",
                  creator: (graphic) => {
                    let btn;
                    btn = document.createElement("button");
                    btn.innerText = t("seeOrder");
                    btn.addEventListener("click", () => {
                      onExit && onExit();
                      setModal("order", order);
                    });
                    return btn;
                  },
                });
              }

              let popupTemplate = {
                title: t("order", {
                  value: order?.order_information?.public_order_number ?? "",
                }),
                content,
              };

              currentGraphics = currentGraphics.filter(
                (graphic) =>
                  graphic?.attributes?.id !== order?.order_information?.item_id
              );
              addGraphic(
                point,
                RecieptSymbol(
                  order?.order_information?.daily_receipt_number,
                  [
                    ...uniqueColor.slice(0, 3),
                    isDelivered ? DELIVERED_OPACITY : 255,
                  ],
                  true
                ),
                popupTemplate,
                {
                  type: "order",
                  id: order?.order_information?.item_id,
                  baseOpacity: isDelivered ? DELIVERED_OPACITY : 255,
                }
              );
            } catch {
              // Ignore order graphic failures so the rest of the map can render.
            }
          });
        currentGraphics.forEach((graphic) =>
          removeGraphics((g) => g.attributes.id === graphic.attributes.id)
        );
      } else {
        removeOrders();
      }

      if (isLoaded && showHeatmap && memoizedOrders.length > 0) {
        loadModules(["esri/layers/FeatureLayer"]).then(([FeatureLayer]) => {
          const features = memoizedOrders
            .map((order, i) => {
              const { lng, lat } = getGeoOrder(order);
              if (!lng || !lat) return null;
              return {
                geometry: {
                  type: "point",
                  longitude: lng,
                  latitude: lat,
                },
                attributes: { ObjectID: i, amount: order.amount },
              };
            })
            .filter(Boolean);

          const heatmapLayer = new FeatureLayer({
            source: features,
            fields: [
              { name: "ObjectID", type: "oid" },
              { name: "amount", type: "double" },
            ],
            objectIdField: "ObjectID",

            renderer: {
              type: "heatmap",
              colorStops: [
                { ratio: 0, color: "rgba(255, 255, 255, 0)" }, // volledig transparant
                { ratio: 0.2, color: "rgba(0, 150, 255, 0.7)" }, // helder blauw
                { ratio: 0.4, color: "rgba(0, 200, 255, 0.8)" }, // cyaan/blauw
                { ratio: 0.6, color: "rgba(0, 255, 127, 0.8)" }, // groen
                { ratio: 0.8, color: "rgba(255, 255, 0, 0.9)" }, // geel
                { ratio: 1, color: "rgba(255, 69, 0, 1)" },
              ],
              maxPixelIntensity: 15,
              minPixelIntensity: 0,
            },
          });

          // eerst oude heatmap verwijderen (als hij bestaat)
          const oldHeatmap = view.map.layers.find((l) => l.title === "Legenda");
          if (oldHeatmap) {
            view.map.remove(oldHeatmap);
          }

          heatmapLayer.title = "Legenda";
          view.map.add(heatmapLayer);
          const legend = new legendsRef.current({
            view,
            layerInfos: [{ layer: heatmapLayer }],
          });
          view.ui.add(legend, "bottom-right");
        });
      } else if (isLoaded && !showHeatmap) {
        // heatmap uitzetten
        const oldHeatmap = view.map.layers.find((l) => l.title === "Legenda");
        if (oldHeatmap) {
          view.map.remove(oldHeatmap);
          view.ui.remove(legendsRef.current);
          const container = document.querySelector(
            ".esri-component.esri-legend.esri-widget.esri-widget--panel"
          );
          if (container) container.remove();
        }
      } else if (isLoaded && memoizedOrders.length === 0 && showHeatmap) {
        const oldHeatmap = view.map.layers.find((l) => l.title === "Legenda");
        if (oldHeatmap) {
          view.map.remove(oldHeatmap);
          view.ui.remove(legendsRef.current);
          const container = document.querySelector(
            ".esri-component.esri-legend.esri-widget.esri-widget--panel"
          );
          if (container) container.remove();
        }
      }
    }
  }, [isLoaded, memoizedOrders, showHeatmap]);

  useEffect(() => {
    if (isLoaded) {
      if (routes !== undefined) {
        if (routes.length !== 0) {
          setFollowCourier(undefined);
          removeRoutes();
          toggleOpacity(255);
          toggleOpacity(55, routes);

          const start = new pointRef.current({
            longitude: branch.gps_longitude,
            latitude: branch.gps_latitude,
          });
          let destination;

          if (routes.length === 1 && orderWithGeo(routes[0])) {
            destination = getOrderDestination(routes?.[0]);
          }

          view.when(() => {
            try {
              routes.forEach((order) => {
                const { lng, lat } = getGeoOrder(order);
                if (lat !== undefined && lng !== undefined) {
                  getRoute(
                    order,
                    start,
                    destination,
                    view.graphics
                      .toArray()
                      .filter((graphic) =>
                        graphic.attributes.type === "branch"
                          ? true
                          : graphic.attributes.id ===
                            order?.order_information?.item_id
                      )
                  );
                }
              });
            } catch {
              // Ignore route processing failures for individual updates.
            }
          });
        } else {
          setFollowCourier(undefined);
          removeRoutes();
          toggleOpacity(255);
        }
      } else {
        toggleOpacity(255);
      }
    }
  }, [routes, isLoaded]);

  useEffect(() => {
    if (isLoaded) {
      if (couriers !== undefined && couriers.length !== 0) {
        view.when(() => {
          removeCouriers();
          couriers.forEach((courier, i) => {
            try {
              const { order, lon, lat, order_id, ride_id, speed, account_id } =
                courier;
              let popupTemplate: any = {
                title: t("courierNoOrder", {
                  courier: courier.account_name,
                }),
              };
              if (order !== "0" && order !== undefined) {
                const asap = order?.order_information?.hand_over_is_asap;
                const handOverTime = order?.order_information?.hand_over_time;

                popupTemplate = {
                  title: courier.account_name,
                  content: [
                    {
                      type: "custom",
                      creator: (graphic) =>
                        `<div>
                                  <p>${getFullAddressV2(order)}</p>
                                  <p>
                                  <div style='display:${
                                    asap ? "block" : "none"
                                  };'>
                                    Z.S.M
                                  </div>
                                  <div style='display:${
                                    handOverTime ? "block" : "none"
                                  };'>
                                  ${formatDate(handOverTime, "time")}
                                  </div>
                                  </p>
                                <div>`,
                    },
                  ],
                };
              }
              addGraphic(
                new pointRef.current({
                  longitude: lon,
                  latitude: lat,
                }),
                courierSymbol(
                  order === "0" || order === undefined
                    ? numberToRGBA(parseInt(account_id))
                    : order?.order_information?.hand_over_is_asap
                      ? [211, 1, 1, 255]
                      : numberToRGBA(parseInt(account_id)),
                  `${courier.account_name} ${
                    speed !== undefined ? `(${speed}km/u)` : ""
                  }`,
                  parseInt(
                    order === "0" || order === undefined
                      ? "0"
                      : order?.order_information?.daily_receipt_number
                  ),
                  getUniqueOrderColor(courier.order),
                  tCommon("toBranch")
                ),
                popupTemplate,
                {
                  type: "courier",
                  courierName: courier.account_name,
                  text: courier.account_name,
                  id: courier.account_id,
                  order: courier.order,
                }
              );
            } catch {
              // Ignore courier graphic failures so other couriers still render.
            }
          });
        });
      } else {
        return removeCouriers();
      }
    }
  }, [isLoaded, couriers]);

  useEffect(() => {
    if (isLoaded) {
      if (followCourier !== undefined) {
        view.when(() => {
          const order: OrderV2 = followCourier.attributes.order;
          toggleOpacity(255);
          toggleOpacity(50, order);
          removeRoutes();
          const graphics = [
            followCourier,
            view.graphics
              .toArray()
              .find(
                (graphic) =>
                  graphic?.attributes?.id ===
                    order?.order_information?.item_id &&
                  graphic.attributes.type === "order"
              ),
          ];
          getRoute(order, followCourier, getOrderDestination(order), graphics);
        });
      } else {
        removeRoutes();
        toggleOpacity(255);
      }
    }
  }, [followCourier, isLoaded]);

  const fitToCouriers = () => {
    if (!isLoaded) return;
    // 1. courier points
    const courierPoints = courierGraphics
      .filter((g) => g.geometry)
      .map(
        (g) =>
          new pointRef.current({
            longitude: g.geometry.longitude,
            latitude: g.geometry.latitude,
            spatialReference: { wkid: 4326 },
          })
      );

    // 2. branch point
    const branchPoint = new pointRef.current({
      longitude: Number(branch?.gps_longitude),
      latitude: Number(branch?.gps_latitude),
      spatialReference: { wkid: 4326 },
    });

    // 3. orders point;
    const ordersPoints =
      asArray(
        view?.graphics
          ?.toArray()
          ?.find((graphic) => graphic.attributes.type === "order")
      ) || [];
    const important = [...courierPoints, ...ordersPoints];
    const all = [...important, branchPoint];

    if (important.length === 0) return;

    // 3. lon/lat arrays
    const lons = all.map((p) => p.longitude);
    const lats = all.map((p) => p.latitude);

    // 4. extent in WGS84
    const rawExtent = new extendRef.current({
      xmin: Math.min(...lons),
      ymin: Math.min(...lats),
      xmax: Math.max(...lons),
      ymax: Math.max(...lats),
      spatialReference: { wkid: 4326 },
    });

    // 5. padding (in degrees, werkt prima voor korte afstanden)
    const pad = 0.002; // ~400–500 meter
    const padded = new extendRef.current({
      xmin: rawExtent.xmin - pad,
      ymin: rawExtent.ymin - pad,
      xmax: rawExtent.xmax + pad,
      ymax: rawExtent.ymax + pad,
      spatialReference: { wkid: 4326 },
    });

    // 6. laat ArcGIS zelf naar WebMercator projecteren
    view.goTo(padded, { duration: 300 });
  };

  // Handler bij user interaction (zoom/drag/click)
  const handleUserInteraction = () => {
    if (autoFitEnabled) setAutoFitEnabled(false);

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      setAutoFitEnabled(true);
    }, 10000); // 10 seconden
  };

  // Bind map events zodra view geladen is
  useEffect(() => {
    if (!isLoaded) return;

    const handleDrag = view.on("drag", handleUserInteraction);
    const handleZoom = view.on("mouse-wheel", handleUserInteraction);
    const handleClick = view.on("click", handleUserInteraction);

    return () => {
      handleDrag.remove();
      handleZoom.remove();
      handleClick.remove();
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [isLoaded]);

  // Automatisch fitten zolang autoFitEnabled actief is
  useEffect(() => {
    if (!isLoaded || !autoFitEnabled || is_auto_zoom_active) return;
    fitToCouriers();
  }, [isLoaded, autoFitEnabled, courierGraphics, is_auto_zoom_active]);

  return <div ref={mapDiv} style={{ height: height }}></div>;
};

export default memo(Map);
